import type { CompRef } from '@wix/document-services-types';
import {
  DATA,
  DisplayGroups,
  EditorElement,
  InlineElement,
  CssProperties,
  CssCustomProperties,
  ComponentInitialSize,
} from '@wix/component-protocol/schema';
import type { EditorReactComponent } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';

import { STYLE_PROPERTIES_TYPES } from '../handlers/constants';
import { ManifestBuilderStrategy } from '../builders';

import { ClientSpecMapService } from '../sharedServices';
import { ChangeType, ListenerPriority } from '../ManifestChangeDetection';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class ComponentsDefinitionsPlatformService extends AbstractComponentsPlatformService {
  private readonly clientSpecMapService: ClientSpecMapService;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.clientSpecMapService = new ClientSpecMapService(
      this.dm,
      this.editorPointerAPI,
    );
  }

  private isInnerElement(
    manifest: EditorElement | InlineElement,
  ): manifest is InlineElement {
    return (manifest as InlineElement).behaviors !== undefined;
  }

  private isInnerElementByEditorPointer(
    editorPointer: EditorPointer<CompRef>,
  ): boolean {
    const manifest = this.manifestStore.getResolvedEditorElement(
      editorPointer,
      { useRawManifest: true },
    );
    return !manifest || this.isInnerElement(manifest);
  }

  getComponentType(editorPointer: EditorPointer<CompRef>) {
    const virtualManifest = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getVirtualManifest();

    return virtualManifest.tree.componentType;
  }

  onChange(
    editorPointers: EditorPointer<CompRef>[],
    cb: (patch: { type: ChangeType; value: unknown }) => void,
  ) {
    return this.manifestChangeDetection.subscribe(
      (patch) => {
        cb({
          type: patch.type,
          value: patch.value,
        });
      },
      {
        editorPointers,
        priority: ListenerPriority.External,
      },
    );
  }

  getEditActions(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .resolver.getActionDefinitions();
  }

  getCustomActions(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ): NonNullable<EditorElement['customActions']> {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .resolver.getCustomActionDefinitions();
  }

  getDisplayGroups(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
    options?: { filter?: { groupType?: DisplayGroups[string]['groupType'] } },
  ) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .resolver.getDisplayGroupDefinitions(options);
  }

  getStyle(editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .resolver.getStyleDefinitions();
  }

  getColorStyle(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ): {
    cssProperties: CssProperties;
    cssCustomProperties: CssCustomProperties;
  } | null {
    const api = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
        strategy: ManifestBuilderStrategy.Merge,
      })
      .getAPI();

    const style = api.resolver.getStyleDefinitions();

    const colorPropertyTypes = Object.values({
      ...STYLE_PROPERTIES_TYPES.COLOR_SUPPORTING_SHORTHANDS,
      ...STYLE_PROPERTIES_TYPES.COLOR,
    });

    if (!style) {
      return null;
    }

    const filteredCssProperties = Object.entries(
      style.cssProperties ?? {},
    ).filter(([key]) => {
      return colorPropertyTypes.includes(key as any);
    });

    const filteredCssCustomProperties = Object.entries(
      style.cssCustomProperties ?? {},
    ).filter(([, item]) => {
      return (
        item?.cssPropertyType &&
        colorPropertyTypes.includes(item.cssPropertyType)
      );
    });

    if (
      filteredCssProperties.length === 0 &&
      filteredCssCustomProperties.length === 0
    ) {
      return null;
    }

    return {
      cssProperties: Object.fromEntries(filteredCssProperties),
      cssCustomProperties: Object.fromEntries(filteredCssCustomProperties),
    };
  }

  getData(editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .resolver.getDataDefinitions();
  }

  getDataRecursively(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ) {
    const api = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI();

    const rootData = api.resolver.getDataDefinitions();
    const innerElementsData = api
      .getChildren({ recursive: true })
      .map(({ resolver }) => resolver.getDataDefinitions());
    return [rootData].concat(innerElementsData);
  }

  /**
   * this method should work only for the single pointer
   * there shouldn't be the case when we call getHelp for multiple components
   * */
  getHelp(editorPointer: EditorPointer<CompRef>) {
    const manifest = this.manifestStore.getResolvedEditorElement(
      editorPointer,
      { useRawManifest: true },
    );
    return manifest?.helpArticle ?? null;
  }

  /**
   * this method should work only for the single pointer
   * there shouldn't be the case when we call getInteractions for multiple components
   * */
  getInteractions(
    editorPointer: EditorPointer<CompRef>,
  ): EditorElement['interactions'] | null {
    const manifest = this.manifestStore.getResolvedEditorElement(
      editorPointer,
      { useRawManifest: true },
    );

    if (!manifest || this.isInnerElement(manifest)) {
      return null;
    }

    return manifest?.interactions ?? null;
  }

  getMediaData(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ) {
    const mediaDataTypes = [
      DATA.DATA_TYPE.video,
      DATA.DATA_TYPE.image,
      DATA.DATA_TYPE.audio,
      DATA.DATA_TYPE.vectorArt,
    ];
    const data = this.getData(editorPointers as EditorPointer<CompRef>[]);

    if (!data) {
      return null;
    }

    const filteredDataEntries = Object.entries(data).filter(([, item]) => {
      return item?.dataType && mediaDataTypes.includes(item.dataType);
    });
    if (filteredDataEntries.length === 0) {
      return null;
    }
    return Object.fromEntries(filteredDataEntries);
  }

  getContentFill(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ): NonNullable<EditorElement['layout']>['contentFill'] {
    const api = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
        strategy: ManifestBuilderStrategy.Merge,
      })
      .getAPI();

    const layoutDefinitions = api.resolver.getLayoutDefinitions();
    return layoutDefinitions?.contentFill;
  }

  getImageData(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ) {
    const data = this.getData(editorPointers as EditorPointer<CompRef>[]);

    if (!data) {
      return null;
    }

    const filteredImageDataEntries = Object.entries(data).filter(
      ([, item]) => item?.dataType === DATA.DATA_TYPE.image,
    );
    if (filteredImageDataEntries.length === 0) {
      return null;
    }
    return Object.fromEntries(filteredImageDataEntries);
  }

  /**
   * this method should work only for the single pointer
   * there shouldn't be the case when we call getContainerData for multiple components
   * */
  getContainerData(editorPointer: EditorPointer<CompRef>) {
    const data = this.getData(editorPointer);

    if (!data) {
      return null;
    }

    const filteredContainerDataEntries = Object.entries(data).filter(
      ([, item]) => item?.dataType === DATA.DATA_TYPE.container,
    );
    if (filteredContainerDataEntries.length === 0) {
      return null;
    }
    return Object.fromEntries(filteredContainerDataEntries);
  }

  getMenuData(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ) {
    const data = this.getData(editorPointers as EditorPointer<CompRef>[]);

    if (!data) {
      return null;
    }

    const filteredMenuDataEntries = Object.entries(data).filter(
      ([, item]) => item?.dataType === DATA.DATA_TYPE.menuItems,
    );
    if (filteredMenuDataEntries.length === 0) {
      return null;
    }
    return Object.fromEntries(filteredMenuDataEntries);
  }

  getAppliedPreset(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .query.getAppliedPreset();
  }

  getPresets(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .resolver.getPresetDefinitions();
  }

  getDisplayName(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ) {
    return this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getAPI()
      .resolver.getDisplayName();
  }

  /**
   * this method should work only for the single pointer
   * there shouldn't be the case when we call getSelector for multiple components
   * */
  getSelector(editorPointer: EditorPointer<CompRef>) {
    const manifest = this.manifestStore.getResolvedEditorElement(
      editorPointer,
      { useRawManifest: true },
    );
    return manifest?.selector ?? null;
  }

  /**
   * this method should work only for the single pointer
   * there shouldn't be the case when we call getArchetype for multiple components
   * */
  getArchetype(editorPointer: EditorPointer<CompRef>) {
    const manifest = this.manifestStore.getResolvedEditorElement(
      editorPointer,
      { useRawManifest: true },
    );
    return manifest?.archetype ?? null;
  }

  /**
   * this method should work only for the single pointer
   * there shouldn't be the case when we call getDefaultDataValue for multiple components
   * */
  getDefaultDataValue(
    editorPointer: EditorPointer<CompRef>,
    dataItemKey: string,
  ) {
    const api = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getAPI();

    return api.resolver.getDefaultDataValue(dataItemKey);
  }

  getDefaultStyleValue(
    editorPointer: EditorPointer<CompRef>,
    styleItemKey: string,
  ) {
    const api = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getAPI();

    return api.resolver.getDefaultStyleValue(styleItemKey);
  }

  getDefaultStyleValues(editorPointer: EditorPointer<CompRef>) {
    const api = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getAPI();

    return api.resolver.getDefaultStyleValues();
  }

  getDefaultCustomStyleValue(
    editorPointer: EditorPointer<CompRef>,
    styleItemKey: string,
  ) {
    const api = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: [editorPointer],
      })
      .getAPI();

    return api.resolver.getDefaultStyleValue(styleItemKey);
  }

  getMetadata(editorPointer: EditorPointer<CompRef>) {
    return this.manifestStore.getMetadataByEditorPointer(editorPointer);
  }

  getInstallation(
    editorPointer: EditorPointer<CompRef>,
  ): EditorReactComponent['installation'] | undefined {
    const compType = this.clientSpecMapService.getCompType(editorPointer);
    const manifest = this.manifestStore.get(compType)?.manifest;
    return manifest?.installation;
  }

  getInitialSize(
    editorPointer: EditorPointer<CompRef>,
  ): ComponentInitialSize | null {
    const isInnerElement = this.isInnerElementByEditorPointer(editorPointer);
    if (isInnerElement) {
      return null;
    }

    const installation = this.getInstallation(editorPointer);
    const appliedPreset = this.getAppliedPreset(editorPointer);
    const presetDefinitions = this.getPresets(editorPointer);

    const presetDefinition = appliedPreset
      ? presetDefinitions?.[appliedPreset]
      : undefined;

    return presetDefinition?.initialSize ?? installation?.initialSize ?? null;
  }
}
